/**
 * Хук для работы с предложениями статей расходов
 */

import { useState, useEffect, useRef } from 'react';
import suggestService, { SuggestionsStatus } from '../services/suggestService';
import cacheService from '../services/cacheService';

interface UseSuggestionsOptions {
  restaurantId?: string;
}

export const useSuggestions = (options?: UseSuggestionsOptions) => {
  const { restaurantId } = options || {};
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SuggestionsStatus | null>(null);
  const hasInitialized = useRef(false);

  /**
   * Загружает предложения статей расходов с кэшированием
   */
  const fetchSuggestions = async (forceRefresh: boolean = false) => {
    // Не загружаем если ресторан не выбран
    if (!restaurantId) {
      setSuggestions([]);
      return { suggestions: [], count: 0 };
    }

    const cacheKey = `suggestions_${restaurantId}`;
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<string[]>(cacheKey);
      if (cached && cached.length > 0) {
        setSuggestions(cached);
        return { suggestions: cached, count: cached.length };
      }
      
      // Skip if we recently got empty results
      if (cacheService.shouldSkipEmptyRequest(cacheKey, 60000)) { // 1 minute threshold
        console.log('Skipping suggestions fetch - recent empty response');
        return { suggestions: [], count: 0 };
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await suggestService.getExpenseSuggestions(restaurantId);
      setSuggestions(data.suggestions);
      
      // Cache the results
      cacheService.set(cacheKey, data.suggestions, 5 * 60 * 1000); // 5 minutes
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка загрузки предложений';
      console.warn('Ошибка загрузки предложений:', errorMessage);
      
      // Если это ошибка API (например, 429), показываем пустой список и кэшируем его
      if (errorMessage.includes('429') || errorMessage.includes('Quota exceeded')) {
        setSuggestions([]);
        setError(null);
        // Cache empty result to prevent repeated failed requests
        cacheService.set(cacheKey, [], 2 * 60 * 1000); // 2 minutes
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Обновляет предложения статей расходов
   */
  const updateSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await suggestService.updateExpenseSuggestions();
      
      // Clear cache and reload suggestions
      cacheService.clear('suggestions');
      await fetchSuggestions(true); // Force refresh
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка обновления предложений';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Получает статус кэша предложений
   */
  const fetchStatus = async () => {
    try {
      const data = await suggestService.getSuggestionsStatus();
      setStatus(data);
      return data;
    } catch (err) {
      console.error('Ошибка получения статуса предложений:', err);
      return null;
    }
  };

  /**
   * Фильтрует предложения по поисковому запросу
   */
  const filterSuggestions = (query: string): string[] => {
    if (!query.trim()) {
      return suggestions;
    }
    
    const lowerQuery = query.toLowerCase();
    return suggestions.filter(suggestion => 
      suggestion.toLowerCase().includes(lowerQuery)
    );
  };

  /**
   * Очищает ошибку
   */
  const clearError = () => {
    setError(null);
  };

  // Загружаем предложения при инициализации хука или изменении restaurantId
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
    fetchSuggestions();
  }, [restaurantId]); // Перезагружаем при смене ресторана

  return {
    suggestions,
    loading,
    error,
    status,
    fetchSuggestions,
    updateSuggestions,
    fetchStatus,
    filterSuggestions,
    clearError
  };
};
